import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import {
  useStockCountSessions,
  useCreateStockCountSession,
  useStartStockCount,
  useSubmitStockCount,
  useApproveStockCount,
  useRejectStockCount,
  useStockCountItems,
  useUpdateCountItem,
  useStockCountSummary,
} from '@/hooks/useStockCount';
import { useLocations } from '@/hooks/useLocations';
import { useCategories } from '@/hooks/useCategories';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Package,
  Search,
  Plus,
  Play,
  Send,
  CheckCircle,
  XCircle,
  ClipboardList,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  pending_review: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const countStatusColors: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  counted: 'bg-blue-100 text-blue-800',
  verified: 'bg-green-100 text-green-800',
  adjusted: 'bg-purple-100 text-purple-800',
};

export default function StockCountPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data: sessions = [], isLoading } = useStockCountSessions({
    status: statusFilter || undefined,
  });
  const { data: locations = [] } = useLocations();
  const { data: categories = [] } = useCategories();

  const createMutation = useCreateStockCountSession();
  const startMutation = useStartStockCount();
  const submitMutation = useSubmitStockCount();
  const approveMutation = useApproveStockCount();
  const rejectMutation = useRejectStockCount();

  const { data: sessionItems } = useStockCountItems(selectedSession?.session_id);
  const { data: sessionSummary } = useStockCountSummary(selectedSession?.session_id);

  const createFields: FormField[] = [
    { name: 'session_name', label: 'Session Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'location_id', label: 'Location', type: 'select',
      options: [{ value: '', label: 'All Locations' }, ...locations.map(l => ({ value: l.location_id, label: l.name }))] },
    { name: 'category_id', label: 'Category', type: 'select',
      options: [{ value: '', label: 'All Categories' }, ...categories.map(c => ({ value: c.category_id, label: c.name }))] },
    { name: 'count_method', label: 'Count Method', type: 'select', required: true,
      options: [
        { value: 'full', label: 'Full Count' },
        { value: 'partial', label: 'Partial Count' },
        { value: 'cycle_count', label: 'Cycle Count' },
      ]},
    { name: 'include_zero_count', label: 'Include Zero Count', type: 'checkbox' },
    { name: 'allow_adjustment', label: 'Allow Auto Adjustment', type: 'checkbox' },
  ];

  const handleCreate = async (data: any) => {
    try {
      await createMutation.mutateAsync(data);
      toast({ title: 'Stock count session created' });
      setIsCreateModalOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleStart = async (sessionId: string) => {
    try {
      await startMutation.mutateAsync(sessionId);
      toast({ title: 'Stock count started' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSubmit = async (sessionId: string) => {
    try {
      await submitMutation.mutateAsync(sessionId);
      toast({ title: 'Stock count submitted for review' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleApprove = async (sessionId: string) => {
    try {
      await approveMutation.mutateAsync({ sessionId });
      toast({ title: 'Stock count approved and adjustments applied' });
      setIsDetailModalOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleReject = async (sessionId: string) => {
    if (!rejectReason) {
      toast({ title: 'Please provide a reason for rejection', variant: 'destructive' });
      return;
    }
    try {
      await rejectMutation.mutateAsync({ sessionId, reason: rejectReason });
      toast({ title: 'Stock count rejected' });
      setIsDetailModalOpen(false);
      setRejectReason('');
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const openDetail = (session: any) => {
    setSelectedSession(session);
    setIsDetailModalOpen(true);
  };

  const columns = [
    { accessorKey: 'session_name', header: 'Session Name' },
    { accessorKey: 'location_name', header: 'Location' },
    { accessorKey: 'category_name', header: 'Category' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: any) => (
        <Badge className={statusColors[row.status] || 'bg-gray-100'}>
          {row.status.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      accessorKey: 'count_method',
      header: 'Method',
      cell: ({ row }: any) => (
        <span className="capitalize">{row.count_method.replace('_', ' ')}</span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }: any) => format(new Date(row.created_at), 'dd MMM yyyy HH:mm'),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => openDetail(row)}>
            View
          </Button>
          {row.status === 'draft' && (
            <Button variant="ghost" size="sm" onClick={() => handleStart(row.session_id)}>
              <Play className="h-4 w-4" />
            </Button>
          )}
          {row.status === 'in_progress' && (
            <Button variant="ghost" size="sm" onClick={() => handleSubmit(row.session_id)}>
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const itemColumns = [
    { accessorKey: 'item_code', header: 'Item Code' },
    { accessorKey: 'item_name', header: 'Item Name' },
    { accessorKey: 'location_name', header: 'Location' },
    { accessorKey: 'system_quantity', header: 'System Qty' },
    {
      accessorKey: 'counted_quantity',
      header: 'Counted',
      cell: ({ row }: any) => row.counted_quantity ?? '-',
    },
    {
      accessorKey: 'variance_quantity',
      header: 'Variance',
      cell: ({ row }: any) => {
        if (!row.variance_quantity) return '-';
        const color = row.variance_quantity > 0 ? 'text-green-600' : row.variance_quantity < 0 ? 'text-red-600' : '';
        return <span className={color}>{row.variance_quantity > 0 ? '+' : ''}{row.variance_quantity}</span>;
      },
    },
    {
      accessorKey: 'count_status',
      header: 'Status',
      cell: ({ row }: any) => (
        <Badge className={countStatusColors[row.count_status] || 'bg-gray-100'}>
          {row.count_status}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Physical Stock Count</h1>
          <p className="text-muted-foreground">Inventory reconciliation and stock take</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Count Session
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ClipboardList className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Sessions</p>
                <p className="text-2xl font-bold">{sessions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold">
                  {sessions.filter((s: any) => s.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Search className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">
                  {sessions.filter((s: any) => s.status === 'pending_review').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">
                  {sessions.filter((s: any) => s.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="in_progress">In Progress</option>
              <option value="pending_review">Pending Review</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <DataTable columns={columns} data={sessions} />
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <ModalForm
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        fields={createFields}
        title="New Stock Count Session"
        isLoading={createMutation.isPending}
      />

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSession?.session_name}</DialogTitle>
          </DialogHeader>
          
          {selectedSession && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-xl font-bold">{sessionSummary?.total_items || 0}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Counted</p>
                  <p className="text-xl font-bold">{sessionSummary?.counted_items || 0}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Variances</p>
                  <p className="text-xl font-bold">{sessionSummary?.items_with_variance || 0}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Variance Value</p>
                  <p className="text-xl font-bold">
                    {sessionSummary?.total_variance_value 
                      ? `$${Number(sessionSummary.total_variance_value).toLocaleString()}`
                      : '$0'}
                  </p>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <h3 className="font-semibold mb-2">Count Items</h3>
                <DataTable columns={itemColumns} data={sessionItems || []} />
              </div>

              {/* Actions */}
              {selectedSession.status === 'pending_review' && (
                <div className="flex gap-4">
                  <Button onClick={() => handleApprove(selectedSession.session_id)} className="flex-1">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve & Adjust
                  </Button>
                  <div className="flex-1 flex gap-2">
                    <input
                      type="text"
                      placeholder="Rejection reason..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="flex-1 px-4 py-2 border rounded-lg"
                    />
                    <Button variant="destructive" onClick={() => handleReject(selectedSession.session_id)}>
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
